// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'
import Cropper from 'react-easy-crop'

// HELPERS
import { stringifyFptiProps, trackClick, usePrefixedContent } from '@/utils'
import { useImpressionTracking } from '@/hooks'

// COMPONENTS
import {
  BodyText,
  Button,
  Divider,
  MoveIcon,
  Sheet,
} from '@paypalcorp/pp-react'
import FileUploader from '../file-uploader'

// CONSTANTS
import { IMAGE_CROPPER_CONTAINER_TEST_ID } from '@/constants/test-ids'

// STYLES
import styles from './styles.module.less'

// TYPES
import { UnformattedFptiOptions } from '@/types'

// TYPES
type CroppedArea = {
  x: number
  y: number
  width: number
  height: number
}
type HandleClose = (...args: unknown[]) => void

/**
 * Displays a screen to crop, edit, or delete image (if editing)
 */
const ImageCropper: React.FC<{
  image: string
  isCircularPhoto?: boolean
  isEditing?: boolean
  title: string
  ariaLabelText: string
  fpti: {
    cropFpti: UnformattedFptiOptions
    changeFpti: UnformattedFptiOptions
    saveFpti: UnformattedFptiOptions
  }
  handleClose: HandleClose
  handleDelete: HandleClose
  handleError: (error: string) => void
  handleResult: (image: string) => void
  handleSave: (croppedImage: string) => void
}> = ({
  image,
  isCircularPhoto,
  isEditing,
  title,
  ariaLabelText,
  fpti,
  handleClose,
  handleDelete,
  handleError,
  handleResult,
  handleSave,
}) => {
  useImpressionTracking(fpti?.cropFpti)

  const [elementToFocusOnOnClose, setElementToFocusOnOnClose] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  })

  useEffect(() => {
    setElementToFocusOnOnClose(document.activeElement)
  }, [])

  const getContent = usePrefixedContent('components.ui.image-cropper.')
  const generalUploadErrorText = getContent('generalUploadError')

  const onCropComplete = (
    _croppedArea: CroppedArea,
    croppedAreaPixels: CroppedArea,
  ) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoom(+e.target.value)
  }

  const handleUploadError = (error: string) => {
    handleError(error)
    onClose()
  }

  function onSave() {
    trackClick(fpti?.saveFpti)
    const imageObj = new Image()
    imageObj.src = image
    imageObj.crossOrigin = 'Anonymous'

    imageObj.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')

        const maxSize = Math.max(imageObj.width, imageObj.height)
        const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2))

        canvas.width = safeArea
        canvas.height = safeArea

        context.translate(safeArea / 2, safeArea / 2)
        context.translate(-safeArea / 2, -safeArea / 2)

        context.drawImage(
          imageObj,
          safeArea / 2 - imageObj.width * 0.5,
          safeArea / 2 - imageObj.height * 0.5,
        )
        const data = context.getImageData(0, 0, safeArea, safeArea)

        canvas.width = croppedAreaPixels.width
        canvas.height = croppedAreaPixels.height

        context.putImageData(
          data,
          Math.round(
            0 - safeArea / 2 + imageObj.width * 0.5 - croppedAreaPixels.x,
          ),
          Math.round(
            0 - safeArea / 2 + imageObj.height * 0.5 - croppedAreaPixels.y,
          ),
        )

        handleSave(canvas.toDataURL())
        elementToFocusOnOnClose.focus()
      } catch (_e) {
        handleError(generalUploadErrorText)
      }
    }
  }

  const onClose = () => {
    handleClose()
    elementToFocusOnOnClose.focus()
  }

  return (
    <Sheet
      isOpen
      skipFormFocus
      mobileFixedHeight={'90'}
      title={title}
      onCloseComplete={onClose}
      closeButtonProps={{ onClick: onClose }}
    >
      <div
        className={styles.imageCropperContainer}
        data-testid={IMAGE_CROPPER_CONTAINER_TEST_ID}
      >
        <div
          className={
            isCircularPhoto
              ? styles.profileCropContainer
              : styles.coverCropContainer
          }
        >
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={false}
            aspect={isCircularPhoto ? 1 : 16 / 6}
            cropShape={isCircularPhoto ? 'round' : 'rect'}
          />
        </div>
      </div>
      <div className={styles.controlsContainer}>
        <div className={styles.positionLabelContainer}>
          <MoveIcon aria-hidden />
          <BodyText>{getContent('dragToPosition')}</BodyText>
        </div>
        <FileUploader
          ariaLabelText={ariaLabelText}
          keyName={`${title}-change-photo`}
          handleResult={handleResult}
          handleError={handleUploadError}
        >
          <Button
            secondary
            data-fpti={stringifyFptiProps(fpti?.changeFpti)}
            className={styles.controlButton}
            tabIndex={-1}
            onClick={() => {
              document
                .getElementById(`${title}-change-photo-file-uploader`)
                .click()
            }}
          >
            {getContent('changePhoto')}
          </Button>
        </FileUploader>
        {isEditing ? (
          <Button
            className={styles.controlButton}
            tertiary
            onClick={handleDelete}
          >
            {getContent('deletePhoto')}
          </Button>
        ) : null}
        <Divider />
        <input
          type='range'
          min='1'
          max='3'
          step={0.1}
          value={zoom}
          onChange={handleZoomChange}
          className={styles.zoomScrollbar}
        />
        <Button onClick={onSave} className={styles.saveButton}>
          {getContent('save')}
        </Button>
      </div>
    </Sheet>
  )
}

export default ImageCropper
